const asArray = value => (Array.isArray(value) ? value : []);

const getNestedApiSubsets = node => {
    const fromBranches = asArray(node?.branches).flatMap(branch => asArray(branch?.subsets));
    const fromRest = asArray(node?.restBranch?.subsets);

    return [...fromBranches, ...fromRest];
};

const getChildSubsets = node => {
    if (Array.isArray(node?.branches)) {
        return getNestedApiSubsets(node);
    }

    return asArray(node?.subsets);
};

const visitBranchStats = (branch, depth, stats) => {
    stats.subsetCount += 1;
    if (depth >= 2) {
        stats.nestingCount += 1;
        stats.hasLimitedScope = true;
    }
    if (branch?.isActive === false) {
        stats.hasInactiveSubset = true;
    }
    if (branch?.isSignificant) {
        stats.hasSignificantChangeSubset = true;
    }
};

const getModelLabel = (model, keys) => {
    if (model == null) {
        return '';
    }
    if (typeof model !== 'object') {
        return String(model);
    }
    return keys.map(key => model[key]).find(Boolean) || '';
};

export const getParameterModels = record => asArray(record?.filters ?? record?.models);

export const getParameterModelIds = record =>
    getParameterModels(record)
        .map(model => (model && typeof model === 'object' ? model.id ?? model.code : model))
        .filter(id => id != null && id !== '');

export const getModelDisplayName = model => getModelLabel(model, ['name', 'code']);

export const getModelTooltipName = model => getModelLabel(model, ['name', 'code']);

export const getOverwriteStats = record => {
    const subsets = asArray(record?.subsets);
    if (subsets.length === 0) {
        return {
            subsetCount: record?.subsetCount ?? record?.overrideCount ?? 0,
            nestingCount: record?.nestingCount ?? record?.nestedOverrideCount ?? 0,
            hasLimitedScope: Boolean(record?.hasLimitedScope),
            hasInactiveSubset: Boolean(record?.hasInactiveSubset),
            hasSignificantChangeSubset: Boolean(record?.hasSignificantChangeSubset ?? record?.hasSignificantSubset),
        };
    }

    const stats = {
        subsetCount: 0,
        nestingCount: 0,
        hasLimitedScope: false,
        hasInactiveSubset: false,
        hasSignificantChangeSubset: false,
    };

    const walk = (nodes, depth) => {
        nodes.forEach(node => {
            const branches = asArray(node?.branches);
            if (branches.length > 0) {
                branches.forEach(branch => {
                    visitBranchStats(branch, depth, stats);
                    const nested = asArray(branch?.subsets);
                    if (nested.length > 0) {
                        stats.hasLimitedScope = true;
                    }
                    walk(nested, depth + 1);
                });
                walk(asArray(node?.restBranch?.subsets), depth + 1);
                return;
            }

            visitBranchStats(node, depth, stats);
            const children = getChildSubsets(node);
            if (children.length > 0) {
                stats.hasLimitedScope = true;
            }
            walk(children, depth + 1);
        });
    };

    walk(subsets, 1);

    return {
        subsetCount: record?.subsetCount ?? record?.overrideCount ?? stats.subsetCount,
        nestingCount: record?.nestingCount ?? record?.nestedOverrideCount ?? stats.nestingCount,
        hasLimitedScope: record?.hasLimitedScope ?? stats.hasLimitedScope,
        hasInactiveSubset: record?.hasInactiveSubset ?? stats.hasInactiveSubset,
        hasSignificantChangeSubset: record?.hasSignificantChangeSubset ?? record?.hasSignificantSubset ?? stats.hasSignificantChangeSubset,
    };
};
